import { Fragment, useCallback, useEffect, useState } from 'react';
import { __CERTIFICATION_API_URL__ } from 'shell/constants/environment';
import Table from 'shell/components/shared/table';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { generatePath, useNavigate } from 'react-router-dom';
import { HttpResponseList } from 'shell/models/http-response';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { initialValues } from '..';
import { useFormik } from 'formik';
import { transformDataset } from 'front-office/utils/type-doc-transform-dataset';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { typeDocCrubs } from 'front-office/constants/type-doc-crubs';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import { headCells, validationSchemaFilter } from 'front-office/constants/type-doc-list';
import ListHeader from 'shell/components/shared/list-header';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { transformeTypeDocList } from 'front-office/components/type-doc/utils/transforme-type-doc-list';
import { TypeDoc } from 'shell/models/type-doc';
import {
  TypeDocClient,
  TypeDocResponse,
  FiltersFormTypeDoc,
} from 'front-office/view-models/type-doc-list';
import { Typography, useEventCallback } from '@mui/material';
import { Order } from 'shell/view-models/order';
import FiltersTypeDoc from 'front-office/components/type-doc/list/filters';
import { FrontOfficeAuthorizations } from 'shell/authorizations/front-office';

const authsObj = Object.freeze({
  read: [FrontOfficeAuthorizations.TipoDocQuery],
  create: [FrontOfficeAuthorizations.TipoDocAdd],
  edit: [FrontOfficeAuthorizations.TipoDocUpdate],
  delete: [FrontOfficeAuthorizations.TipoDocDelete],
});

const TypeDocList = (): React.ReactElement => {
  const deleteTypeDoc = useHttpClient(``, {
    options: {
      errorCatching: true,
      method: 'delete',
      loading: true,
      data: {},
    },
  });

  const [list, getList] = useHttpFetch<HttpResponseList<TypeDoc>, TypeDocResponse>(
    `${__CERTIFICATION_API_URL__}/certificazione/tipi-documento`,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
        data: {},
      },
      transformer: transformeTypeDocList,
    }
  );

  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<TypeDocClient>>({
    element: null,
    open: false,
  });

  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<TypeDocClient>>({
    element: null,
    open: false,
  });

  const filterTable = useFilterTable<FiltersFormTypeDoc>();

  const onSubmit = useEventCallback((formValues: FiltersFormTypeDoc) => {
    const queryString = transformDataset({ ...formValues, page: 0 });
    filterTypeDocForm.setFieldValue('page', 0);
    getList({
      params: {},
      paramsSerializer: () => queryString,
    });
  });

  const filterTypeDocForm = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchemaFilter,
    onSubmit,
  });

  useEffect(() => {
    return () => filterTable.setValues(filterTypeDocForm.values);
  }, [filterTypeDocForm.values]);

  const navigate = useNavigate();

  const auths = useActionAuthorization(authsObj);

  const onVisibilityPress = useCallback(
    (cell): Promise<TypeDocClient> => {
      return new Promise((resolve) => {
        navigate(
          generatePath('/sportello/tipo-doc/dettaglio/:id/:idDoc', {
            id: cell.code,
            idDoc: cell.codeTypeDoc,
          })
        );
        resolve(cell);
      });
    },
    [navigate]
  );

  const onEditPress = useCallback(
    (cell): Promise<TypeDocClient> => {
      return new Promise((resolve) => {
        navigate(
          generatePath('/sportello/tipo-doc/modifica/:id/:idDoc', {
            id: cell?.code,
            idDoc: cell?.codeTypeDoc,
          })
        );
        resolve(cell);
      });
    },
    [navigate]
  );

  const onTrashPress = useEventCallback((cell: TypeDocClient): Promise<TypeDocClient> => {
    return new Promise((resolve) => {
      setDeleteAlertModal({ open: true, element: cell });
      resolve(cell);
    }) as Promise<TypeDocClient>;
  });

  const handleChangePage = useEventCallback((newPage: number) => {
    filterTypeDocForm.setFieldValue('page', newPage);
  });

  const handleChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    const newDataset = {
      ...filterTypeDocForm.values,
      rowsPerPage: rowsPerPage || 0,
      page: 0,
    };
    filterTypeDocForm.setValues(newDataset);
  });

  const onSort = useEventCallback((orders: Order) => {
    const newDataset = { ...filterTypeDocForm.values, orders };
    const queryString = transformDataset(newDataset);
    getList({
      params: {},
      paramsSerializer: () => queryString,
    });
    filterTypeDocForm.setFieldValue('orders', orders);
  });

  return (
    <Fragment>
      <Breadcrumbs items={typeDocCrubs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuovo tipo documento"
        addBtnLabel="Aggiungi nuovo tipo documento"
        canAdd={auths.create}
        onAddBtnClick={() => {
          navigate('/sportello/tipo-doc/nuovo');
        }}
        title="Gestione Tipo documento"
      />
      <div className="pmi-my-3">
        <FiltersTypeDoc filterForm={filterTypeDocForm} />
      </div>
      {list.data ? (
        (((list.data as TypeDocResponse)?.list as Array<TypeDocClient>) || [])?.length ? (
          <Table
            onSort={onSort}
            head={headCells}
            orders={filterTypeDocForm.values.orders}
            activeActions={auths}
            page={filterTypeDocForm.values.page}
            rowsPerPage={filterTypeDocForm.values.rowsPerPage}
            body={((list.data as TypeDocResponse)?.list as Array<TypeDocClient>) || []}
            onVisibilityPress={onVisibilityPress}
            onEditPress={onEditPress}
            onTrashPress={onTrashPress}
            count={(list.data as TypeDocResponse)?.count || 0}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ariaLabel="Lista Tipi documenti"
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
      <DeleteAlertModal
        title="Cancellazione tipo documento"
        message="Confermare la cancellazione?"
        open={deleteAlertModal.open}
        onClose={() => setDeleteAlertModal({ open: false, element: null })}
        onYes={() => {
          deleteTypeDoc({
            url: `${__CERTIFICATION_API_URL__}/certificazione/tipi-documento/${deleteAlertModal.element?.codeTypeDoc}/${deleteAlertModal.element?.code}`,
          });
          setDeleteAlertModal({ open: false, element: null });
          setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
        }}
        onNo={() => setDeleteAlertModal({ open: false, element: null })}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione tipo documento"
        message={`La cancellazione di tipo documento ${deleteSuccessModal.element?.description} è avvenuta con successo.`}
        onClose={() => {
          const queryString = transformDataset({ ...filterTypeDocForm.values, page: 0 });
          getList({
            params: {},
            paramsSerializer: () => queryString,
          });
          setDeleteSuccessModal({ open: false, element: null });
        }}
        onOk={() => {
          const queryString = transformDataset({ ...filterTypeDocForm.values, page: 0 });
          getList({
            params: {},
            paramsSerializer: () => queryString,
          });
          setDeleteSuccessModal({ open: false, element: null });
        }}
      />
    </Fragment>
  );
};

TypeDocList.displayName = 'Type Doc List page component';

export default TypeDocList;
